// @flow
// Chaque course a un fuseau horaire (dénormalisé depuis son driver_group à
// la création, cf. l'API). L'heure de départ doit toujours désigner une
// heure locale dans CE fuseau, quel que soit le fuseau du navigateur de la
// personne qui la saisit (passager) ou qui la consulte (chauffeur).
import moment from "moment-timezone";

// Par défaut quand aucun fuseau n'est connu (course/driver_group pas encore
// chargé, donnée historique antérieure à ce champ) : la flotte française.
export const RIDE_TIMEZONE = "Europe/Paris";

/**
 * Extrait le fuseau horaire d'une course déjà créée. Le champ dénormalisé
 * ride.time_zone prime (il fige le fuseau au moment de la course même si le
 * driver_group change de réglage ensuite) ; à défaut on retombe sur le
 * driver_group populé, puis sur Europe/Paris.
 */
export function getRideTimezone(ride: ?Object): string {
	if (!ride) return RIDE_TIMEZONE;
	return ride.time_zone || (ride.driver_group && ride.driver_group.time_zone) || RIDE_TIMEZONE;
}

/**
 * Extrait le fuseau horaire d'un driver group (formulaires de création, où
 * la course n'existe pas encore).
 */
export function getDriverGroupTimezone(driverGroup: ?Object): string {
	return (driverGroup && driverGroup.time_zone) || RIDE_TIMEZONE;
}

/**
 * Sérialise une valeur de date/heure sélectionnée dans un DateTimePicker en
 * heure de départ finale. Le picker restitue l'heure telle que tapée à
 * l'écran (ex: "14:00"), interprétée avec le fuseau du navigateur — on
 * réancre ces mêmes chiffres sur le fuseau de la course plutôt que de les
 * convertir avec l'offset local de la personne qui les saisit.
 * Une valeur déjà sérialisée (chaîne ISO chargée depuis l'API, non modifiée
 * par l'utilisateur) représente déjà le bon instant : elle est ré-exportée
 * telle quelle, sans réinterprétation.
 */
export function toRideTimeISOString(value: mixed, timezone: string = RIDE_TIMEZONE): ?string {
	if (!value) return value;
	if (typeof value === "string") return moment(value).toISOString();
	const wallClock = moment(value).format("YYYY-MM-DD HH:mm:ss");
	return moment.tz(wallClock, "YYYY-MM-DD HH:mm:ss", timezone).toISOString();
}

/**
 * Retourne un moment ancré sur le fuseau de la course, pour affichage — quel
 * que soit le fuseau du navigateur de la personne qui consulte la course.
 * Sans date, retourne "maintenant" dans ce fuseau.
 */
export function momentInRideTimezone(date: mixed, timezone: string = RIDE_TIMEZONE): Object {
	return date ? moment.tz(date, timezone) : moment.tz(timezone);
}

/**
 * Formate directement une date stockée dans le fuseau de la course.
 */
export function formatInRideTimezone(date: mixed, format: string, timezone: string = RIDE_TIMEZONE): ?string {
	if (!date) return null;
	return momentInRideTimezone(date, timezone).format(format);
}
